import React, { useState } from "react";
import {
  Stack,
  Button,
  Typography,
  TextField,
  MenuItem,
  Divider,
  Paper,
  Box,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

/**
 * Fluxo guiado Meta / Google (OAuth, listagem, seleção) — padrão próximo a Canais.
 * Token manual fica em «Avançado» para não sobrecarregar o passo principal.
 */
export default function PaidAdsProviderSetup({
  provider,
  connections,
  metaAccounts = [],
  metaAccountPick,
  onMetaAccountPickChange,
  googleCustomers = [],
  googleCustomerPick,
  onGoogleCustomerPickChange,
  onOpenMetaOAuth,
  onOpenGoogleOAuth,
  onLoadMetaAccounts,
  onLoadGoogleCustomers,
  onSaveMetaAccount,
  onSaveGoogleCustomer,
  onDisconnectMeta,
  onDisconnectGoogle,
  metaManualToken = "",
  onMetaManualTokenChange,
  onSubmitMetaManualToken,
}) {
  const { t } = useTranslation("settings");
  const [tokenOpen, setTokenOpen] = useState(false);

  if (provider === "meta") {
    const metaReady = Boolean(connections?.meta?.connected) && Boolean(connections?.meta?.adAccountId);
    return (
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("paidAdsPage.metaStep1Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("paidAdsPage.metaStep1Body")}
          </Typography>
          <Button variant="contained" onClick={onOpenMetaOAuth}>
            {t("paidAdsPage.connectMeta")}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("paidAdsPage.metaStep2Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("paidAdsPage.metaStep2Body")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={onLoadMetaAccounts}>
              {t("paidAdsPage.listAdAccounts")}
            </Button>
          </Stack>
          {metaAccounts.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("paidAdsPage.adAccount")}
                value={metaAccountPick}
                onChange={(e) => onMetaAccountPickChange?.(e.target.value)}
              >
                {metaAccounts.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.name || a.id}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" color="primary" onClick={onSaveMetaAccount}>
                {t("paidAdsPage.saveSelection")}
              </Button>
            </Stack>
          )}
        </Paper>

        <Box>
          <Button
            size="small"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: tokenOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            }
            onClick={() => setTokenOpen((v) => !v)}
            sx={{ mb: tokenOpen ? 1 : 0 }}
          >
            {t("paidAdsPage.advancedTokenSection")}
          </Button>
          <Collapse in={tokenOpen}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t("paidAdsPage.advancedTokenSectionHint")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
              <TextField
                size="small"
                fullWidth
                type="password"
                autoComplete="off"
                label={t("paidAdsPage.metaTokenPasteLabel")}
                placeholder={t("paidAdsPage.metaTokenPastePlaceholder")}
                value={metaManualToken}
                onChange={(e) => onMetaManualTokenChange?.(e.target.value)}
              />
              <Button
                variant="outlined"
                onClick={onSubmitMetaManualToken}
                disabled={!String(metaManualToken || "").trim()}
                sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}
              >
                {t("paidAdsPage.metaTokenPasteSubmit")}
              </Button>
            </Stack>
          </Collapse>
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="body2">
            {t("paidAdsPage.status")}:{" "}
            <Box component="span" fontWeight={600} color={metaReady ? "success.main" : "text.secondary"}>
              {metaReady ? t("paidAdsPage.connected") : t("paidAdsPage.disconnected")}
            </Box>
          </Typography>
          <Button size="small" color="error" variant="outlined" onClick={onDisconnectMeta} disabled={!connections?.meta?.connected}>
            {t("paidAdsPage.disconnect")}
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (provider === "google") {
    const googleReady = Boolean(connections?.google?.connected) && Boolean(connections?.google?.customerId);
    return (
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("paidAdsPage.googleStep1Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("paidAdsPage.googleStep1Body")}
          </Typography>
          <Button variant="contained" onClick={onOpenGoogleOAuth}>
            {t("paidAdsPage.connectGoogle")}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("paidAdsPage.googleStep2Title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("paidAdsPage.googleStep2Body")}
          </Typography>
          <Button variant="outlined" onClick={onLoadGoogleCustomers}>
            {t("paidAdsPage.listCustomers")}
          </Button>
          {googleCustomers.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("paidAdsPage.customer")}
                value={googleCustomerPick}
                onChange={(e) => onGoogleCustomerPickChange?.(e.target.value)}
              >
                {googleCustomers.map((c) => (
                  <MenuItem key={c.customerId || c.resourceName} value={String(c.customerId || "")}>
                    {c.customerId || c.resourceName}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={onSaveGoogleCustomer}>
                {t("paidAdsPage.saveSelection")}
              </Button>
            </Stack>
          )}
        </Paper>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="body2">
            {t("paidAdsPage.status")}:{" "}
            <Box component="span" fontWeight={600} color={googleReady ? "success.main" : "text.secondary"}>
              {googleReady ? t("paidAdsPage.connected") : t("paidAdsPage.disconnected")}
            </Box>
          </Typography>
          <Button size="small" color="error" variant="outlined" onClick={onDisconnectGoogle} disabled={!connections?.google?.connected}>
            {t("paidAdsPage.disconnect")}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return null;
}
